import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from "@stripe/react-stripe-js/checkout";
import { PrimaryButton, formatCurrency } from "../UI/DesignStandards";
import {
  quoteDollars,
  quoteSignupPayment,
} from "../../utility/signupPaymentQuote";
import { isDueCartLine } from "../../utility/signupCart";
import {
  confirmSignupCheckout,
  createSignupCheckout,
  getPayNowStatus,
} from "../../utility/signupPayments";
import { readPayNowStatusPayload } from "../../utility/payNowStatus";
import SignupAgreementNote from "./SignupAgreementNote";

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const QuoteRow = ({ label, amountCents, emphasize = false }) => (
  <div
    className={`flex justify-between items-baseline gap-4 ${
      emphasize
        ? "font-display font-bold text-base mt-3 pt-3 border-t border-brand-charcoal/10"
        : "text-sm text-brand-charcoal"
    }`}
  >
    <span className={emphasize ? "" : "text-brand-muted"}>{label}</span>
    <span className="font-score tabular-nums shrink-0">
      {formatCurrency(quoteDollars(amountCents))}
    </span>
  </div>
);

const ConfirmPay = ({
  tournamentId,
  sessionId,
  onPaid,
  onError,
  showLegalNote = true,
}) => {
  const checkout = useCheckoutElements();
  const [paying, setPaying] = useState(false);
  if (checkout.type === "success") {
    void checkout.checkout.total?.total?.amount;
  }

  const handlePay = async (event) => {
    event?.preventDefault();
    if (checkout.type !== "success") return;
    setPaying(true);
    try {
      const result = await checkout.checkout.confirm({
        redirect: "if_required",
      });
      if (result?.type === "error") {
        onError(result.error?.message || "Card was not charged.");
        return;
      }
      try {
        const confirmation = await confirmSignupCheckout({
          tournamentId,
          sessionId,
        });
        onPaid(confirmation?.paid ? "paid" : "pending", confirmation);
      } catch (_) {
        onPaid("pending");
      }
    } catch (error) {
      onError(error.message || "Card was not charged.");
    } finally {
      setPaying(false);
    }
  };

  if (checkout.type === "error") {
    return (
      <p className="text-sm text-red-800">
        {checkout.error?.message || "Could not load card form."}
      </p>
    );
  }

  return (
    <form className="space-y-4" onSubmit={handlePay}>
      <PaymentElement />
      {showLegalNote ? <SignupAgreementNote /> : null}
      <PrimaryButton
        className="w-full py-3"
        type="submit"
        loading={paying}
        disabled={checkout.type !== "success" || paying}
      >
        Pay now
      </PrimaryButton>
    </form>
  );
};

const PayNowPanel = ({
  tournamentId,
  lines,
  user,
  onPaid = () => {},
  payNowEnabled: payNowEnabledProp,
  statusKnown: statusKnownProp,
  hideTitle = false,
  showLegalNote = true,
}) => {
  const dueLines = (lines || []).filter(isDueCartLine);
  const quote = quoteSignupPayment(dueLines, {
    tournamentId,
    userId: user?.id || null,
    email: user?.email || null,
  });
  const [payNowEnabledState, setPayNowEnabledState] = useState(false);
  const [loadingStatusState, setLoadingStatusState] = useState(true);
  const [statusError, setStatusError] = useState("");
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [checkout, setCheckout] = useState(null);
  const [payState, setPayState] = useState(null);
  const statusFromParent = statusKnownProp !== undefined;
  const payNowEnabled = statusFromParent
    ? Boolean(payNowEnabledProp)
    : payNowEnabledState;
  const loadingStatus = statusFromParent
    ? !statusKnownProp
    : loadingStatusState;

  useEffect(() => {
    if (statusFromParent) return undefined;
    let cancelled = false;
    const load = async () => {
      if (!tournamentId || !dueLines.length) {
        setPayNowEnabledState(false);
        setStatusError("");
        setLoadingStatusState(false);
        return;
      }
      try {
        const data = await getPayNowStatus(tournamentId);
        const result = readPayNowStatusPayload(data);
        if (cancelled) return;
        if (result.known) {
          setPayNowEnabledState(result.payNowEnabled);
          setStatusError("");
        } else {
          setPayNowEnabledState(false);
          setStatusError(result.error || "Could not check card payments.");
        }
      } catch (statusErr) {
        if (!cancelled) {
          const result = readPayNowStatusPayload(null, statusErr);
          setPayNowEnabledState(false);
          setStatusError(result.error);
        }
      } finally {
        if (!cancelled) setLoadingStatusState(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, dueLines.length, statusFromParent]);

  const stripePromise = useMemo(() => {
    if (!publishableKey || !checkout?.stripeAccountId) return null;
    return loadStripe(publishableKey, {
      stripeAccount: checkout.stripeAccountId,
    });
  }, [checkout?.stripeAccountId]);

  if (payState === "paid") {
    return (
      <p className="text-sm font-display font-bold text-brand-charcoal">
        Paid.
      </p>
    );
  }
  if (payState === "pending") {
    return (
      <p className="text-sm text-brand-muted">
        Payment is still processing.
      </p>
    );
  }
  if (!dueLines.length) return null;

  const startCheckout = async () => {
    setStarting(true);
    setError("");
    try {
      const data = await createSignupCheckout({
        tournamentId,
        signupIds: quote.signupIds,
      });
      setCheckout(data);
    } catch (startError) {
      setError(startError.message || "Could not start Pay now.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div
      className={`${
        payNowEnabled || hideTitle ? "" : "pt-4 border-t border-brand-charcoal/10 "
      }space-y-1`}
    >
      {hideTitle ? null : (
        <div className="font-display font-bold text-brand-charcoal pb-2">
          Pay now
        </div>
      )}
      <div className="space-y-2">
        {quote.lines.map((line) => (
          <QuoteRow
            key={line.signupId || line.label}
            label={line.label}
            amountCents={line.advertisedCents}
          />
        ))}
        <QuoteRow label="Platform fee" amountCents={quote.platformFeeCents} />
        <QuoteRow
          label="Card processing"
          amountCents={quote.processingFeeCents}
        />
        <QuoteRow
          label="Pay now total"
          amountCents={quote.amountCents}
          emphasize
        />
      </div>
      {error ? <p className="text-sm text-red-800 pt-2">{error}</p> : null}
      {loadingStatus ? (
        <p className="text-sm text-brand-muted pt-3">Checking card payments…</p>
      ) : statusError ? (
        <p className="text-sm text-red-800 pt-3">{statusError}</p>
      ) : !payNowEnabled ? (
        <p className="text-sm text-brand-muted pt-3">
          Pay at the desk. To pay by card, ask the director to turn on Card
          payments.
        </p>
      ) : checkout?.clientSecret && stripePromise ? (
        <div className="pt-4">
          <CheckoutElementsProvider
            stripe={stripePromise}
            options={{ clientSecret: checkout.clientSecret }}
          >
            <ConfirmPay
              tournamentId={tournamentId}
              sessionId={checkout.sessionId}
              onPaid={(status, confirmation) => {
                setPayState(status === "paid" ? "paid" : "pending");
                onPaid(status, confirmation);
              }}
              onError={(message) => setError(message)}
              showLegalNote={showLegalNote}
            />
          </CheckoutElementsProvider>
        </div>
      ) : (
        <div className="pt-4 space-y-3">
          <PrimaryButton
            className="w-full py-3"
            onClick={startCheckout}
            loading={starting}
          >
            Pay now
          </PrimaryButton>
          {showLegalNote ? <SignupAgreementNote /> : null}
        </div>
      )}
    </div>
  );
};

export default PayNowPanel;
